<template>
    <div v-if="can.view">
        <div class="flex-row items-end mb-32">
            <settings-heading
                :title="$t('settings.users.title')"
                :description="$t('settings.users.description')"
                :button="can.manage && (!currentCompany.uses_sso || !currentCompany.use_sso_jit) ? $t('settings.users.invite_user') : undefined"
                button-icon="add-circle"
                @button-click="create"
            >
                <more-info-button v-if="can.manage && currentCompany.uses_sso" button-text="users.sso_cant_add">
                    <template #content>
                        <p v-html="$t('users.sso_cant_add_details')"></p>
                    </template>
                </more-info-button>
            </settings-heading>

            <toggle v-if="force2faAvailable" v-model="force2fa" disable="fetchingForce2fa" @change="force2faChanged">
                {{ $t('settings.users.force_2fa') }}
            </toggle>
        </div>

        <div class="mb-48">

            <loading v-if="fetchingUsers" class="pt-16"></loading>

            <div v-else>
                <div class="px-16 flex gap-16 uppercase text-body-sm text-primary-80 border-b-2 border-primary-20">
                    <div class="w-1/2">
                        <div class="flex items-center gap-16">
                            <avatar size="small" class="opacity-0" />
                            <p class="font-semibold">{{ $t('settings.users.user') }}</p>
                        </div>
                    </div>
                    <div class="flex-grow flex items-center">
                        <p class="font-semibold">{{ $t('settings.users.role') }}</p>
                    </div>
                </div>

                <div class="overflow-auto">
                    <div
                        v-for="user in users"
                        :key="user.id"
                        class="flex items-center gap-16 border-b border-primary-20 p-16 odd:bg-primary-5"
                    >
                        <div class="w-1/2">
                            <div class="flex items-center gap-16">
                                <avatar :image="user.image_url" size="small">
                                    {{ user.initials }}
                                </avatar>
                                <div class="flex flex-col">
                                    <span class="text-primary font-semibold flex items-center space-x-4">
                                       <span>{{ user.name }}</span>

                                        <tooltip
                                            v-if="user.two_factor_enabled"
                                            :content="$t('auth.secured_with_two_factor')"
                                            position="bottom"
                                        >
                                            <svg-icon size="20" class="fill-semantic-success -mt-2" type="gdpr" />
                                        </tooltip>
                                    </span>
                                    <span>{{ user.email }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="flex-grow">
                            <div v-if="user.id === currentUser.id">
                                {{ roleInMemberships(user.companyUsers).name }}
                            </div>
                            <div v-else>
                                {{ roleNameInCompany(user) }}
                            </div>
                        </div>

                        <div class="flex gap-8">
                            <simple-button
                                v-if="can.manage"
                                icon="edit"
                                look="icon"
                                :title="editTitle"
                                @click="edit(user)"
                            />

                            <simple-button
                                v-if="can.manage"
                                display="danger"
                                icon="delete"
                                look="icon"
                                :title="removeTitle"
                                @click="confirmDelete(user)"
                            />
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <user-editor v-if="editing" :all-users="users" :current-company="currentCompany" :user="editing"
                     @close="stopEdit()"></user-editor>
        <user-editor v-if="creating" :all-users="users" :current-company="currentCompany"
                     @close="stopCreate()"></user-editor>

        <popup-modal-confirm
            v-if="deleting"
            :confirmText="$t('general.remove_subject', {subject: $t('settings.users.user')})"
            :message="confirmDeleteMessage(deleting)"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="removeUser(deleting)">
        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="confirmingForce2FA"
            confirmButton="success"
            :title="$t('settings.users.confirm_force_2fa_title')"
            :message="$t('settings.users.confirm_force_2fa_message')"
            :confirmText="$t('settings.users.confirm_force_2fa')"
            @close="cancelForce2FA"
            @confirm="confirmForce2FA">
        </popup-modal-confirm>
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import * as users from '../../services/UserService';
import { force2fa, updateForce2fa } from '../../services/CompanyService';
import * as companyUsers from '../../services/CompanyUserService';
import User from '../../models/User';
import Loading from '../../components/ui/Loading.vue';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import {normalizeJson} from '../../../shared/helpers';
import Avatar from '../../components/ui/Avatar.vue';
import SvgIcon from '../../components/ui/SvgIcon.vue';
import UserEditor from '../../components/ui/UserEditor.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import Error403 from '../../components/ui/Error403.vue';
import Tooltip from '../../components/navigation/Tooltip';
import MoreInfoButton from "../../components/ui/MoreInfoButton.vue";
import Toggle from '../../components/forms/Toggle.vue';
import Company from '../../models/Company';
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

export default {
    components: {
        SettingsHeading,
        MoreInfoButton,
        Tooltip,
        'loading': Loading,
        'simple-button': SimpleButton,
        'avatar': Avatar,
        'svg-icon': SvgIcon,
        'user-editor': UserEditor,
        'popup-modal-confirm': PopupModalConfirm,
        'error-403': Error403,
        'toggle': Toggle,
    },
    data: function () {
        return {
            fetchingUsers: true,
            editing: null,
            creating: false,
            deleting: null,
            performingDeleteRequest: false,
            force2fa: false,
            fetchingForce2fa: true,
            confirmingForce2FA: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'currentCompanyUser', 'currentUser']),
        ...mapState(useAuthenticationStore, ['currentCompanyId']),
        users() {
            return useRepo(User)
                .with('companyUsers', (q) => {
                    q.with('role')
                })
                .orderBy('first_name')
                .get();
        },
        editTitle() {
            return this.$t('general.edit_subject', {subject: this.$t('settings.users.user')});
        },
        removeTitle() {
            return this.$t('general.remove_subject', {subject: this.$t('settings.users.user')});
        },
        can() {
            return {
                view: this.$can('settings.general'),
                manage: this.$can('users.manage'),
            };
        },
        force2faAvailable() {
            return this.currentCompanyUser?.is_owner && (!this.currentCompany.uses_sso || !this.currentCompany.use_sso_jit);
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        create() {
            stopHsTracking();
            this.creating = true;
        },
        stopCreate() {
            startHsTracking();
            this.creating = false;
        },
        edit(user) {
            stopHsTracking();
            this.editing = user;
        },
        stopEdit() {
            startHsTracking();
            this.editing = null;
        },
        confirmDelete(user) {
            this.deleting = user;
        },
        cancelDelete() {
            this.deleting = null;
        },
        removeUser(user) {
            this.performingDeleteRequest = true;

            let companyUser = user.companyUsers.find(cu => cu.company_id === this.currentCompanyId);

            companyUsers.destroy(companyUser.id).then(() => {
                useRepo(User).destroy(user.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.removed', {subject: this.$t('settings.users.user')}),
                });
            });
        },
        confirmDeleteMessage(user) {
            return this.$t('general.confirm_remove_subject', {subject: user.first_name});
        },
        roleInMemberships(memberships) {
            for (let membership of memberships) {
                if (membership.role !== null) {
                    return membership.role;
                }
            }
        },
        roleNameInCompany(user) {
            return user.companyUsers.find(cu => cu.company_id === this.currentCompanyId).role.name;
        },
        isOwnerInCompany(user) {
            return user.companyUsers.find(cu => cu.company_id === this.currentCompanyId).is_owner;
        },
        force2faChanged(checked) {
            if (checked) {
                this.confirmingForce2FA = true
            } else {
                this.setForce2FA(checked)
            }
        },
        initForce2FA() {
            this.force2fa = this.force2faAvailable &&
                useRepo(Company).find(this.currentCompanyId).force_2fa;
        },
        confirmForce2FA() {
            this.confirmingForce2FA = false
            this.setForce2FA(true)
        },
        setForce2FA(value) {
            updateForce2fa(JSON.stringify({'force_2fa': value}))
                .then(result => {
                    return useRepo(Company).save({'id': this.currentCompanyId, 'force_2fa': value});
                })
        },
        cancelForce2FA() {
            this.confirmingForce2FA = false
            this.force2fa = false
        },
    },
    mounted() {
        this.fetchingUsers = true;

        users.index().then(result => {
            useRepo(User).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingUsers = false;
        });

        this.initForce2FA();
    },
};
</script>
